"use client"

import { useState, useMemo } from "react"
import { toast } from "sonner"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { Search, Filter, MoreHorizontal, Eye, Pencil, Send, CheckCircle2, Archive, XCircle, Download } from "lucide-react"
import { useCases } from "@/lib/mock-data"
import { ClassificationBadge, FeasibilityBadge, PriorityBadge, StatusBadge } from "@/components/status-badges"
import { UseCaseDetailDrawer } from "@/components/use-case-detail-drawer"
import type { AIUseCase, DataClassification, FeasibilityFlag, PriorityBand, UseCaseStatus } from "@/lib/types"

const ALL = "All"

const departments = [ALL, ...Array.from(new Set(useCases.map((u) => u.department)))]
const statusOptions: (UseCaseStatus | typeof ALL)[] = [ALL, "Draft", "Submitted", "AI Mapped", "Feasibility Pending", "Feasibility Completed", "Prioritized", "Approved for Discovery", "In POC", "In Delivery", "Live", "Rejected", "Parked"]
const classificationOptions: (DataClassification | typeof ALL)[] = [ALL, "Public", "L0", "L1"]
const feasibilityOptions: (FeasibilityFlag | typeof ALL)[] = [ALL, "Green", "Amber", "Red"]
const priorityBandOptions: (PriorityBand | typeof ALL)[] = [ALL, "P1", "P2", "P3", "P4"]

export function CentralRegisterContent() {
  const [search, setSearch] = useState("")
  const [filterDept, setFilterDept] = useState(ALL)
  const [filterStatus, setFilterStatus] = useState(ALL)
  const [filterClass, setFilterClass] = useState(ALL)
  const [filterFeasibility, setFilterFeasibility] = useState(ALL)
  const [filterPriority, setFilterPriority] = useState(ALL)
  const [selectedUseCase, setSelectedUseCase] = useState<AIUseCase | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const filtered = useMemo(() => {
    return useCases.filter((uc) => {
      if (filterDept !== ALL && uc.department !== filterDept) return false
      if (filterStatus !== ALL && uc.status !== filterStatus) return false
      if (filterClass !== ALL && uc.dataClassification !== filterClass) return false
      if (filterFeasibility !== ALL && uc.feasibilityFlag !== filterFeasibility) return false
      if (filterPriority !== ALL && uc.priorityBand !== filterPriority) return false
      if (search) {
        const q = search.toLowerCase()
        return (
          uc.name.toLowerCase().includes(q) ||
          uc.id.toLowerCase().includes(q) ||
          uc.department.toLowerCase().includes(q) ||
          uc.businessOwner.toLowerCase().includes(q)
        )
      }
      return true
    })
  }, [search, filterDept, filterStatus, filterClass, filterFeasibility, filterPriority])

  function handleAction(action: string, ucName: string) {
    const messages: Record<string, string> = {
      "approve": `Approved "${ucName}" for Discovery.`,
      "review": `"${ucName}" sent for review.`,
      "park": `"${ucName}" has been parked.`,
      "reject": `"${ucName}" has been rejected.`,
    }
    toast.success(messages[action] ?? `Action performed on "${ucName}".`)
  }

  return (
    <div className="p-6 flex flex-col gap-4">
      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
              <Input
                placeholder="Search use cases..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 text-sm h-9"
              />
            </div>
            <Select value={filterDept} onValueChange={setFilterDept}>
              <SelectTrigger className="w-40 text-xs h-9">
                <Filter className="size-3 mr-1 text-muted-foreground" />
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                {departments.map((d) => <SelectItem key={d} value={d} className="text-xs">{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-44 text-xs h-9">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterClass} onValueChange={setFilterClass}>
              <SelectTrigger className="w-32 text-xs h-9">
                <SelectValue placeholder="Classification" />
              </SelectTrigger>
              <SelectContent>
                {classificationOptions.map((c) => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterFeasibility} onValueChange={setFilterFeasibility}>
              <SelectTrigger className="w-32 text-xs h-9">
                <SelectValue placeholder="Feasibility" />
              </SelectTrigger>
              <SelectContent>
                {feasibilityOptions.map((f) => <SelectItem key={f} value={f} className="text-xs">{f}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-28 text-xs h-9">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                {priorityBandOptions.map((p) => <SelectItem key={p} value={p} className="text-xs">{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              className="h-9 text-xs gap-1.5"
              onClick={() => toast.info("Export CSV coming soon.")}
            >
              <Download className="size-3.5" />
              Export
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Count */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          Showing <span className="font-semibold text-foreground">{filtered.length}</span> of {useCases.length} use cases
        </span>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-xs font-medium text-muted-foreground px-5 py-3 whitespace-nowrap">ID</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Use Case Name</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3 hidden md:table-cell">Department</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3 hidden lg:table-cell">Business Owner</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3 hidden xl:table-cell">AI Owner</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3 hidden lg:table-cell">Classification</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3 hidden md:table-cell">Feasibility</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Score</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3 hidden lg:table-cell">Priority</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3 hidden xl:table-cell">Updated</th>
                  <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="text-center py-12 text-muted-foreground text-sm">
                      No use cases match the current filters.
                    </td>
                  </tr>
                ) : (
                  filtered.map((uc, i) => (
                    <tr key={uc.id} className={`border-b border-border last:border-0 ${i % 2 === 0 ? "" : "bg-muted/20"}`}>
                      <td className="px-5 py-3">
                        <span className="text-xs font-mono text-muted-foreground whitespace-nowrap">{uc.id}</span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => { setSelectedUseCase(uc); setDrawerOpen(true) }}
                          className="text-xs font-medium text-foreground hover:text-primary text-left line-clamp-2 max-w-[200px]"
                        >
                          {uc.name}
                        </button>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-xs text-muted-foreground">{uc.department}</span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-xs text-muted-foreground truncate max-w-[140px] block">{uc.businessOwner}</span>
                      </td>
                      <td className="px-4 py-3 hidden xl:table-cell">
                        <span className="text-xs text-muted-foreground">{uc.aiOwner}</span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <ClassificationBadge value={uc.dataClassification} />
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <FeasibilityBadge value={uc.feasibilityFlag} />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-base font-bold text-foreground">{uc.priorityScore}</span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <PriorityBadge value={uc.priorityBand} />
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge value={uc.status} />
                      </td>
                      <td className="px-4 py-3 hidden xl:table-cell">
                        <span className="text-xs text-muted-foreground">{uc.updatedAt}</span>
                      </td>
                      <td className="px-4 py-3">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="size-7">
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem className="text-xs gap-2" onClick={() => { setSelectedUseCase(uc); setDrawerOpen(true) }}>
                              <Eye className="size-3.5" />View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-xs gap-2" onClick={() => toast.info("Edit coming soon.")}>
                              <Pencil className="size-3.5" />Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-xs gap-2" onClick={() => handleAction("review", uc.name)}>
                              <Send className="size-3.5" />Send for Review
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-xs gap-2 text-emerald-700" onClick={() => handleAction("approve", uc.name)}>
                              <CheckCircle2 className="size-3.5" />Approve for Discovery
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-xs gap-2 text-amber-700" onClick={() => handleAction("park", uc.name)}>
                              <Archive className="size-3.5" />Park
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-xs gap-2 text-destructive" onClick={() => handleAction("reject", uc.name)}>
                              <XCircle className="size-3.5" />Reject
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <UseCaseDetailDrawer
        useCase={selectedUseCase}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  )
}
